import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from '../../icons';
import { Modal, ModalActions, ModalFootNote } from '../../components/shared';
import { CardRadio } from '../../components/shared/Radio';
import { formatWorkMinutes } from '../../lib/daily-work';
import { extractErrorMessage } from '../../api/approval';
import { invalidateWidgetCache } from '../../lib/widget-cache';
import { ApprovalLineBlock, EMPTY_LINE, type ApprovalLineValue } from '../ApprovalPage/ApprovalLineBlock';
import {
  createSubstitute, listSubstituteCandidates,
  type SubstituteCandidateDto, type SubstitutePolicyDto, type SubstituteSubmitDto,
} from '../../api/hr-substitute';
import './Substitute.css';

/**
 * 휴일대체 신청 — 보상휴가(근기법 §57) · 판 B (2026-09-06).
 *
 * ## ★★★ 왜 다른 여섯과 달리 팝업인가
 *
 * 신청 줄의 나머지 여섯은 **양식만 고르면 되는** 신청이라 기안 화면으로 보낸다. 이것은
 * **「어느 날 근로를 바꿀지」를 서버가 알려 줘야** 하는 신청이다 — 승인된 연장·휴일근로가
 * 있는 날만 바꿀 수 있고, 그날이 몇 시간으로 바뀌는지는 가산 배수가 정한다. 사람이 적을 수
 * 있는 값이 아니라서 **후보 목록**이 먼저 온다.
 *
 * ## ★★★ 화면은 숫자를 보내지 않는다
 *
 * 보내는 것은 «날짜 · 사유» 뿐이다. 「6시간 → 9시간」은 **미리보기**이고, 원장에 박히는 값은
 * 서버가 다시 센다 — 화면이 보낸 숫자를 믿으면 그 숫자가 곧 휴가가 된다.
 *
 * ## ★★ 정본
 * `Modal`(A·팝업) · **`CardRadio`(B6 — 선택지가 설명을 동반할 때)** · `ApprovalLineBlock`(A6).
 * 후보 한 줄이 「9/13(토) 휴일근로」 + 「6시간 → 대체휴가 9시간」이라, 설명 없는 라디오로는
 * **뜻을 모르고 고르게 된다**(정본 B6 이 그 자리를 이미 적어 두었다).
 */

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

function ymOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y as number, (m as number) - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** 「9월 13일(토)」 — 표기는 F18(앞자리 채움 없는 날짜)과 같은 결. */
export function candidateDateText(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const day = new Date(Date.UTC(y as number, (m as number) - 1, d as number)).getUTCDay();
  return `${m}월 ${d}일(${WEEKDAY[day]})`;
}

/**
 * 분 → 「9시간」 · 「1시간 30분」 — 서버 `spanText` 와 **같은 말**이어야 한다.
 *
 * ★ 표기는 **`lib/daily-work.ts` 의 `formatWorkMinutes` 한 곳**을 쓴다 (2026-09-26).
 *   견본 22개에서 **글자가 전부 같았다** — 옮겨도 보이는 글자가 안 바뀐다(회귀 0).
 */
export function minutesText(minutes: number): string {
  return formatWorkMinutes(Math.max(0, Math.round(minutes)));
}

/**
 * 그날 무엇을 바꾸는가 — 「휴일근로」 · 「연장근로」 · 「휴일근로 + 야간」.
 *
 * ★ **원천 실측이 0 인 갈래는 안 적는다.** 회사가 그 갈래를 껐거나 그날 없었던 것이고,
 *   둘 다 「그 시간은 안 바뀐다」는 뜻이라 적으면 거짓이 된다.
 */
export function candidateKindText(c: SubstituteCandidateDto): string {
  const parts: string[] = [];
  if (c.holidayMinutes > 0 || c.holidayOverMinutes > 0) parts.push('휴일근로');
  if (c.overtimeMinutes > 0) parts.push('연장근로');
  if (c.nightMinutes > 0) parts.push('야간');
  return parts.length > 0 ? parts.join(' + ') : '가산 근로';
}

/** 원천 실측 합(분) — 「6시간 → 9시간」의 왼쪽. */
export function sourceMinutes(c: SubstituteCandidateDto): number {
  return c.overtimeMinutes + c.holidayMinutes + c.holidayOverMinutes;
}

/** 기한을 사람 말로 — 정책이 「다음 해 말」인지 「N개월」인지. */
export function expireText(policy: SubstitutePolicyDto): string {
  return policy.expireRule === 'MONTHS'
    ? `쓸 수 있는 기한은 일한 날부터 ${policy.expireMonths}개월입니다.`
    : '쓸 수 있는 기한은 일한 해의 다음 해 12월 31일까지입니다.';
}

export function SubstituteRequestModal({ onClose }: { onClose: () => void }) {
  const uid = useId();
  const [month, setMonth] = useState(() => ymOf(new Date()));
  const [items, setItems] = useState<SubstituteCandidateDto[] | null>(null);
  const [policy, setPolicy] = useState<SubstitutePolicyDto | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [line, setLine] = useState<ApprovalLineValue>(EMPTY_LINE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approval, setApproval] = useState<SubstituteSubmitDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    listSubstituteCandidates(month)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setPolicy(res.policy);
        // ★ 달을 옮기면 고른 것을 **버린다** — 지난달 날짜가 골라진 채로 남으면 안 된다.
        setPicked(res.items.length === 1 ? res.items[0]!.date : null);
      })
      .catch((err) => { if (!cancelled) { setItems([]); setError(extractErrorMessage(err)); } });
    return () => { cancelled = true; };
  }, [month]);

  const target = useMemo(
    () => (items ?? []).find((c) => c.date === picked) ?? null,
    [items, picked],
  );

  const submit = useCallback(async () => {
    if (!target) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await createSubstitute({
        workDate: target.date,
        reason: reason.trim() || '휴일·연장근로 대체',
        ...(line.approvers.length > 0 ? { approverIds: line.approvers.map((a) => a.userId) } : {}),
        ...(line.referrers.length > 0 ? { referrerIds: line.referrers.map((r) => r.userId) } : {}),
      });
      invalidateWidgetCache('hr:*');
      // ★ 결재자를 못 찾았으면 **닫지 않는다** — 닫으면 올라간 줄 안다(근태 신청 모달과 같은 규칙).
      if (res.approval?.blocked) {
        setApproval(res.approval);
        return;
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [target, reason, line, onClose]);

  return (
    <Modal
      title="보상휴가 신청"
      size="md"
      onClose={onClose}
      footer={(
        <>
          <ModalFootNote>
            {target
              ? <>적립 <b>{target.creditText}</b> · 신청하면 결재로 올라갑니다</>
              : <>바꿀 근로를 고르면 신청할 수 있습니다</>}
          </ModalFootNote>
          <ModalActions>
            <button type="button" className="dw-btn" onClick={onClose}>취소</button>
            <button
              type="button"
              className="dw-btn dw-btn--primary"
              onClick={() => { void submit(); }}
              disabled={!target || submitting}
            >
              {submitting ? '신청 중…' : '신청'}
            </button>
          </ModalActions>
        </>
      )}
    >
      {error && <div className="dw-alert" role="alert">{error}</div>}
      {approval?.blocked && (
        <div className="dw-alert" role="alert">
          신청은 저장했지만 결재로 올리지 못했습니다 — {approval.reason ?? '조직에서 결재자를 찾지 못했습니다.'}
        </div>
      )}

      <div className="wcsub__monthbar">
        <button
          type="button" className="dw-btn wcsub__navbtn"
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
          aria-label="이전 달"
        >
          <ChevronLeft size={15} aria-hidden />
        </button>
        <span className="wcsub__month" aria-live="polite">
          {month.slice(0, 4)}년 {Number(month.slice(5, 7))}월
        </span>
        <button
          type="button" className="dw-btn wcsub__navbtn"
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
          aria-label="다음 달"
        >
          <ChevronRight size={15} aria-hidden />
        </button>
      </div>

      <div className="dw-field">
        <span className="dw-field__label" id={`${uid}-pick`}>바꿀 근로</span>
        {items === null && <p className="wcsub__note">불러오는 중…</p>}
        {items !== null && items.length === 0 && (
          <p className="wcsub__note">
            {policy?.enabled === false
              ? <>이 회사는 보상휴가를 쓰지 않습니다 — 근태 담당자가 켜야 신청할 수 있습니다.</>
              : <>이 달에는 바꿀 수 있는 근로가 없습니다 — <b>승인된 연장 · 휴일근로</b>만 바꿀 수 있고,
                이미 신청한 날은 다시 낼 수 없습니다.</>}
          </p>
        )}
        {items !== null && items.length > 0 && (
          <div className="wcsub__list" role="radiogroup" aria-labelledby={`${uid}-pick`}>
            {items.map((c) => (
              <CardRadio
                key={c.date}
                name={`${uid}-cand`}
                checked={picked === c.date}
                onChange={() => setPicked(c.date)}
                disabled={submitting}
                title={<>{candidateDateText(c.date)} <span className="wcsub__kind">{candidateKindText(c)}</span></>}
                description={<>{minutesText(sourceMinutes(c))} → 보상휴가 <b>{c.creditText}</b></>}
              />
            ))}
          </div>
        )}
      </div>

      {/* ★ 「왜 6시간이 9시간이 되나」는 도움말이다(정본 P8) — 판정 자체는 위 줄이 이미 적었다. */}
      {policy?.enabled && (
        <p className="wcsub__note">
          §57 보상휴가는 <b>임금을 갈음</b>하는 것이라 <b>가산까지 넣은 시간</b>으로 쌓입니다
          (휴일근로 6시간 → 9시간). 그만큼은 <b>「연장 정산」의 예상 수당에서 빠집니다</b>.
          {' '}{expireText(policy)}
        </p>
      )}

      <div className="dw-field">
        <label className="dw-field__label" htmlFor={`${uid}-reason`}>사유</label>
        <textarea
          id={`${uid}-reason`} className="dw-input dw-input--area" rows={2}
          value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="휴일·연장근로 대체"
        />
      </div>

      {/* 결재선 — 기안 화면·근태 신청 모달과 **같은 조각**이다 (결재 A6). */}
      <ApprovalLineBlock
        docType="SUBSTITUTE_LEAVE"
        value={line}
        onChange={setLine}
        disabled={submitting}
        onError={setError}
      />
    </Modal>
  );
}
